import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth } from "../context/AuthContext";
import { roomsAPI } from "../services/api";
import toast from "react-hot-toast";
import MapPicker from "../components/ui/MapPicker";
import Pagination from "../components/ui/Pagination";
import { Room, RoomFormData } from "../types/room";

const PAGE_SIZE = 10;

function toSlug(str: string): string {
  return str
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/đ/g, "d").replace(/Đ/g, "d")
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-");
}

function RoomsPage(): React.ReactElement {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "superadmin";
  const canEdit = user?.role === "superadmin" || user?.role === "admin";
  const [rooms, setRooms] = useState<Room[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [editingRoom, setEditingRoom] = useState<Room | null>(null);
  const [uploading, setUploading] = useState<boolean>(false);
  const [search, setSearch] = useState<string>("");
  const [filterDistrict, setFilterDistrict] = useState<string>("all");
  const [page, setPage] = useState(1);

  const filteredRooms = useMemo(() => {
    return rooms.filter((r) => {
      const matchSearch = !search || r.name.toLowerCase().includes(search.toLowerCase()) || r.slug.toLowerCase().includes(search.toLowerCase());
      const matchDistrict = filterDistrict === "all" || r.district === filterDistrict;
      return matchSearch && matchDistrict;
    });
  }, [rooms, search, filterDistrict]);

  useEffect(() => { setPage(1); }, [search, filterDistrict]);

  const totalPages = Math.ceil(filteredRooms.length / PAGE_SIZE);
  const pageRooms = useMemo(
    () => filteredRooms.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [filteredRooms, page]
  );

  // Danh sách quận/huyện
  const districts = [
    "Quận 1",
    "Quận 2",
    "Quận 3",
    "Quận 4",
    "Quận 5",
    "Quận 6",
    "Quận 7",
    "Quận 8",
    "Quận 9",
    "Quận 10",
    "Quận 11",
    "Quận 12",
    "Quận Tân Bình",
    "Quận Tân Phú",
    "Quận Phú Nhuận",
    "Quận Bình Thạnh",
    "Quận Gò Vấp",
    "Thủ Đức",
  ];

  // Danh sách tiện nghi
  const amenitiesList = [
    { vi: "Máy lạnh", en: "Air conditioner" },
    { vi: "Máy chiếu", en: "Projector" },
    { vi: "Bếp", en: "Kitchen" },
    { vi: "Nước nóng lạnh", en: "Hot & cold water" },
    { vi: "Tủ lạnh", en: "Refrigerator" },
    { vi: "Máy giặt", en: "Washing machine" },
    { vi: "TV", en: "Television" },
    { vi: "WiFi", en: "WiFi" },
    { vi: "Netflix", en: "Netflix" },
    { vi: "YouTube Premium", en: "YouTube Premium" },
    { vi: "Bồn tắm", en: "Bathtub" },
    { vi: "Ban công", en: "Balcony" },
    { vi: "Bãi đậu xe", en: "Parking" },
    { vi: "PlayStation 4", en: "PlayStation 4" },
    { vi: "PlayStation 5", en: "PlayStation 5" },
  ];

  const [formData, setFormData] = useState<RoomFormData>({
    slug: "",
    name: "",
    address: "",
    addressEn: "",
    district: "",
    districtEn: "",
    location: { lat: "", lng: "" },
    video: "",
    calendarEmbedUrl: "",
    images: [],
    amenities: [],
    amenitiesEn: [],
    priceImage: "",
    price: {
      pricePer4Hours: "",
      pricePer9Hours: "",
      priceOvernight13Hours: "",
      dailyRate: "",
      dailyRateHoliday: "",
    },
    description: "",
    descriptionEn: "",
    checkInInstructions: "",
    checkInInstructionsEn: "",
    checkOutInstructions: "",
    checkInGuide: {
      lockBoxCode: "", lockBoxLocation: "", lockBoxVideoUrl: "", roomNumber: "",
      gateInstructions: "", buildingInstructions: "",
      wifiName: "", wifiPassword: "", parkingInfo: "", googleMapsUrl: "", addressHint: "",
    },
  });

  const fetchRooms = useCallback(async () => {
    try {
      const response = await roomsAPI.getAll();
      let allRooms: Room[] = response.data.data || [];
      // Roommaster: only show managed rooms
      if (user?.role === "roommaster" && user?.managedRooms && Array.isArray(user.managedRooms)) {
        const managedIds = user.managedRooms.map((r) => (typeof r === "object" && r._id ? r._id : r as string));
        allRooms = allRooms.filter((room) => managedIds.includes(room._id));
      }
      setRooms(allRooms);
      setLoading(false);
    } catch (error) {
      toast.error("Lỗi khi tải danh sách phòng");
      setLoading(false);
    }
  }, [user]);

  // Fetch rooms
  useEffect(() => {
    fetchRooms();
  }, [fetchRooms]);

  // Hàm nén ảnh
  const compressImage = (file: File, maxWidth = 1200, quality = 0.7): Promise<string> => {
    const supportedTypes = ["image/jpeg", "image/png", "image/webp"];
    const mimeType = supportedTypes.includes(file.type) ? file.type : "image/jpeg";

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(new Error("Lỗi khi đọc file ảnh"));
      reader.onload = (e) => {
        const img = new Image();
        img.onerror = () => reject(new Error("Lỗi khi tải ảnh"));
        img.onload = () => {
          const canvas = document.createElement("canvas");
          let width = img.width;
          let height = img.height;

          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext("2d");
          if (!ctx) { reject(new Error("Canvas context unavailable")); return; }
          ctx.drawImage(img, 0, 0, width, height);

          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error("Lỗi khi nén ảnh"));
                return;
              }
              const compressedReader = new FileReader();
              compressedReader.onerror = () => reject(new Error("Lỗi khi đọc ảnh đã nén"));
              compressedReader.onloadend = () => {
                resolve(compressedReader.result as string);
              };
              compressedReader.readAsDataURL(blob);
            },
            mimeType,
            quality,
          );
        };
        img.src = e.target?.result as string;
      };
      reader.readAsDataURL(file);
    });
  };

  // Tính kích thước base64 (MB)
  const getBase64Size = (base64String: string): number => {
    const sizeInBytes = (base64String.length * 3) / 4;
    return sizeInBytes / (1024 * 1024);
  };

  // Open modal
  const handleAddNew = (): void => {
    setEditingRoom(null);
    setFormData({
      slug: "",
      name: "",
      address: "",
      addressEn: "",
      district: "",
      districtEn: "",
      location: { lat: "", lng: "" },
      video: "",
      calendarEmbedUrl: "",
      images: [],
      amenities: [],
      amenitiesEn: [],
      priceImage: "",
      price: {
        pricePer4Hours: "",
        pricePer9Hours: "",
        priceOvernight13Hours: "",
        dailyRate: "",
        dailyRateHoliday: "",
      },
      description: "",
      descriptionEn: "",
      checkInInstructions: "",
      checkInInstructionsEn: "",
      checkOutInstructions: "",
      checkInGuide: {
        lockBoxCode: "", lockBoxLocation: "", lockBoxVideoUrl: "", roomNumber: "",
        gateInstructions: "", buildingInstructions: "",
        wifiName: "", wifiPassword: "", parkingInfo: "", googleMapsUrl: "", addressHint: "",
      },
    });
    setShowModal(true);
  };

  const handleEdit = async (room: Room): Promise<void> => {
    setEditingRoom(room);

    const hasLat = room.location?.lat != null && room.location.lat !== "";
    const hasLng = room.location?.lng != null && room.location.lng !== "";
    let location = { lat: hasLat ? String(room.location!.lat) : "", lng: hasLng ? String(room.location!.lng) : "" };

    // Nếu phòng chưa có tọa độ, tự động geocode từ địa chỉ
    if (!hasLat || !hasLng) {
      try {
        const q = encodeURIComponent(`${room.address}, ${room.district}, Thành phố Hồ Chí Minh, Việt Nam`);
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?q=${q}&format=json&limit=1&countrycodes=vn`,
          { headers: { "Accept-Language": "vi,en;q=0.9" } }
        );
        const data = await res.json();
        if (data.length > 0) {
          location = { lat: String(data[0].lat), lng: String(data[0].lon) };
        }
      } catch {
        // ignore, sẽ để trống
      }
    }

    setFormData({
      slug: room.slug,
      name: room.name,
      address: room.address,
      addressEn: room.addressEn || "",
      district: room.district,
      districtEn: room.districtEn || "",
      location,
      video: room.video || "",
      calendarEmbedUrl: room.calendarEmbedUrl || "",
      images: room.images || [],
      amenities: room.amenities || [],
      amenitiesEn: room.amenitiesEn || [],
      priceImage: room.priceImage || "",
      price: {
        pricePer4Hours: room.price.pricePer4Hours,
        pricePer9Hours: room.price.pricePer9Hours,
        priceOvernight13Hours: room.price.priceOvernight13Hours,
        dailyRate: room.price.dailyRate,
        dailyRateHoliday: room.price.dailyRateHoliday,
      },
      description: room.description,
      descriptionEn: room.descriptionEn || "",
      checkInInstructions: room.checkInInstructions || "",
      checkInInstructionsEn: room.checkInInstructionsEn || "",
      checkOutInstructions: room.checkOutInstructions || "",
      checkInGuide: {
        lockBoxCode:          room.checkInGuide?.lockBoxCode || "",
        lockBoxLocation:      room.checkInGuide?.lockBoxLocation || "",
        lockBoxVideoUrl:      room.checkInGuide?.lockBoxVideoUrl || "",
        roomNumber:           room.checkInGuide?.roomNumber || "",
        gateInstructions:     room.checkInGuide?.gateInstructions || "",
        buildingInstructions: room.checkInGuide?.buildingInstructions || "",
        wifiName:             room.checkInGuide?.wifiName || "",
        wifiPassword:         room.checkInGuide?.wifiPassword || "",
        parkingInfo:          room.checkInGuide?.parkingInfo || "",
        googleMapsUrl:        room.checkInGuide?.googleMapsUrl || "",
        addressHint:          room.checkInGuide?.addressHint || "",
      },
    });
    setShowModal(true);
  };

  const handleToggleFeatured = async (room: Room): Promise<void> => {
    if (!room.featured) {
      const featuredCount = rooms.filter((r) => r.featured).length;
      if (featuredCount >= 6) {
        toast.error("Tối đa 6 phòng nổi bật. Bỏ chọn một phòng trước.");
        return;
      }
    }
    try {
      await roomsAPI.toggleFeatured(room._id);
      fetchRooms();
    } catch {
      toast.error("Lỗi khi cập nhật");
    }
  };

  const handleToggleHidden = async (room: Room): Promise<void> => {
    try {
      await roomsAPI.toggleHidden(room._id);
      toast.success(room.hidden ? "Đã hiện phòng" : "Đã ẩn phòng");
      fetchRooms();
    } catch {
      toast.error("Lỗi khi cập nhật");
    }
  };

  const handleDelete = async (id: string): Promise<void> => {
    if (window.confirm("Bạn có chắc muốn xóa phòng này?")) {
      try {
        await roomsAPI.delete(id);
        toast.success("Đã xóa phòng thành công");
        fetchRooms();
      } catch (error) {
        toast.error("Lỗi khi xóa phòng");
      }
    }
  };

  // Upload ảnh phòng lên Cloudinary
  const handleImagesUpload = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setUploading(true);
    toast.loading(`Đang upload ${files.length} ảnh lên Cloudinary...`);

    try {
      const uploadedUrls: string[] = [];

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const compressed = await compressImage(file);
        const blob = await fetch(compressed).then(r => r.blob());
        const fd = new FormData();
        fd.append("image", blob, file.name);
        fd.append("roomName", formData.name || "room");

        const res = await fetch(
          `${process.env.REACT_APP_API_URL || "http://localhost:5000/api"}/upload/room-image`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
            body: fd,
          }
        );
        const data = await res.json();
        if (data.success) {
          uploadedUrls.push(data.url);
          toast.loading(`Đang upload ${i + 1}/${files.length}...`);
        } else {
          toast.error(`Lỗi upload ảnh ${i + 1}: ${data.message}`);
        }
      }

      toast.dismiss();
      toast.success(`✅ Đã upload ${uploadedUrls.length} ảnh lên Cloudinary`);
      setFormData({ ...formData, images: [...formData.images, ...uploadedUrls] });
    } catch (error) {
      toast.dismiss();
      toast.error("Lỗi khi upload ảnh");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  // Upload ảnh bảng giá lên Cloudinary (folder: Price-Room/TenPhong)
  const handlePriceImageUpload = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    toast.loading("Đang upload ảnh bảng giá...");

    try {
      const compressed = await compressImage(file);
      const blob = await fetch(compressed).then(r => r.blob());
      const fd = new FormData();
      fd.append("image", blob, file.name);
      fd.append("roomName", formData.name || "room");
      fd.append("folderPrefix", "Price-Room");
      // Gửi URL cũ để backend xóa ảnh cũ trên Cloudinary
      if (formData.priceImage) fd.append("oldUrl", formData.priceImage);

      const res = await fetch(
        `${process.env.REACT_APP_API_URL || "http://localhost:5000/api"}/upload/room-image`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
          body: fd,
        }
      );
      const data = await res.json();
      toast.dismiss();
      if (data.success) {
        toast.success("✅ Đã upload ảnh bảng giá");
        setFormData({ ...formData, priceImage: data.url });
      } else {
        toast.error("Lỗi upload: " + data.message);
      }
    } catch (error) {
      toast.dismiss();
      toast.error("Lỗi khi upload ảnh");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeImage = (index: number): void => {
    const newImages = formData.images.filter((_, i) => i !== index);
    setFormData({ ...formData, images: newImages });
  };

  const toggleAmenity = (amenity: { vi: string; en: string }): void => {
    const isSelected = formData.amenities.includes(amenity.vi);
    if (isSelected) {
      setFormData({
        ...formData,
        amenities: formData.amenities.filter((a) => a !== amenity.vi),
        amenitiesEn: formData.amenitiesEn.filter((a) => a !== amenity.en),
      });
    } else {
      setFormData({
        ...formData,
        amenities: [...formData.amenities, amenity.vi],
        amenitiesEn: [...formData.amenitiesEn, amenity.en],
      });
    }
  };

  const handleDistrictChange = (district: string): void => {
    const districtEnMap: Record<string, string> = {
      "Quận 1": "District 1",
      "Quận 2": "District 2",
      "Quận 3": "District 3",
      "Quận 4": "District 4",
      "Quận 5": "District 5",
      "Quận 6": "District 6",
      "Quận 7": "District 7",
      "Quận 8": "District 8",
      "Quận 9": "District 9",
      "Quận 10": "District 10",
      "Quận 11": "District 11",
      "Quận 12": "District 12",
      "Quận Tân Bình": "Tan Binh District",
      "Quận Tân Phú": "Tan Phu District",
      "Quận Phú Nhuận": "Phu Nhuan District",
      "Quận Bình Thạnh": "Binh Thanh District",
      "Quận Gò Vấp": "Go Vap District",
      "Thủ Đức": "Thu Duc City",
    };
    setFormData({
      ...formData,
      district,
      districtEn: districtEnMap[district] || district,
    });
  };

  const handleSave = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();

    // Tính tổng kích thước trước khi lưu
    let totalSize = 0;
    formData.images.forEach((img) => {
      totalSize += getBase64Size(img);
    });

    if (totalSize > 16) {
      toast.error(
        `❌ Không thể lưu! Tổng kích thước ${totalSize.toFixed(2)}MB vượt giới hạn 16MB!`,
      );
      return;
    }

    try {
      if (editingRoom) {
        await roomsAPI.update(editingRoom._id, formData as unknown as Record<string, unknown>);
        toast.success("Cập nhật phòng thành công");
      } else {
        await roomsAPI.create(formData as unknown as Record<string, unknown>);
        toast.success("Thêm phòng thành công");
      }
      setShowModal(false);
      fetchRooms();
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Có lỗi xảy ra");
    }
  };

  const formatPrice = (price: number): string => {
    return new Intl.NumberFormat("vi-VN", {
      style: "currency",
      currency: "VND",
    }).format(price);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-coffee-600 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-zinc-900 tracking-tight">Quản lý phòng</h1>
          <p className="text-sm text-zinc-500 mt-0.5">Quản lý tất cả các phòng homestay</p>
        </div>
        {canEdit && (
          <button
            onClick={handleAddNew}
            className="flex items-center gap-2 px-4 py-2 bg-coffee-600 hover:bg-coffee-700 text-white text-sm font-medium rounded-lg transition-colors"
          >
            <span className="text-base leading-none">+</span>
            Thêm phòng mới
          </button>
        )}
      </div>

      {/* Search & Filter */}
      <div className="bg-white rounded-xl border border-zinc-200 px-4 py-3 flex flex-wrap gap-3 items-center">
        <input
          type="text"
          placeholder="Tìm tên phòng, slug..."
          value={search}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
          className="flex-1 min-w-[200px] px-3 py-1.5 border border-zinc-200 rounded-lg text-sm focus:outline-none focus:border-coffee-400 focus:ring-1 focus:ring-coffee-200"
        />
        <select
          value={filterDistrict}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setFilterDistrict(e.target.value)}
          className="px-3 py-1.5 border border-zinc-200 rounded-lg text-sm focus:outline-none focus:border-coffee-400 bg-white text-zinc-700"
        >
          <option value="all">Tất cả quận/huyện</option>
          {districts.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
        {(search || filterDistrict !== "all") && (
          <button
            onClick={() => { setSearch(""); setFilterDistrict("all"); }}
            className="text-xs text-coffee-600 hover:text-coffee-800 font-medium"
          >
            Xóa bộ lọc
          </button>
        )}
        <span className="text-xs text-zinc-400 ml-auto tabular-nums">
          {filteredRooms.length} / {rooms.length} phòng
        </span>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-zinc-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="border-b border-zinc-100">
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wide">Ảnh</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wide">Phòng</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wide">Slug</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wide">Địa chỉ</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-zinc-400 uppercase tracking-wide">4h</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-zinc-400 uppercase tracking-wide">9h</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-zinc-400 uppercase tracking-wide">Qua đêm</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-zinc-400 uppercase tracking-wide">Ngày</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-zinc-400 uppercase tracking-wide">T6-CN</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-zinc-400 uppercase tracking-wide">
                  Nổi bật ({rooms.filter(r => r.featured).length}/6)
                </th>
                {canEdit && (
                  <th className="px-4 py-3 text-center text-xs font-medium text-zinc-400 uppercase tracking-wide">Thao tác</th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-50">
              {pageRooms.map((room) => (
                <tr key={room._id} className={`hover:bg-zinc-50 transition-colors ${room.hidden ? "opacity-40" : ""}`}>
                  <td className="px-4 py-3">
                    <img
                      src={room.images?.[0] || "https://via.placeholder.com/150"}
                      alt={room.name}
                      className="w-14 h-14 object-cover rounded-lg border border-zinc-100"
                      onError={(e: React.SyntheticEvent<HTMLImageElement>) =>
                        (e.currentTarget.src = "https://via.placeholder.com/150?text=No+Image")
                      }
                    />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-zinc-900">{room.name}</span>
                      {room.hidden && (
                        <span className="text-[10px] font-medium px-1.5 py-0.5 bg-zinc-100 text-zinc-500 rounded">Ẩn</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs font-mono text-zinc-500 bg-zinc-50 px-2 py-0.5 rounded border border-zinc-100">{room.slug}</span>
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-sm text-zinc-700 font-medium">{room.district}</p>
                    <p className="text-xs text-zinc-400 mt-0.5 max-w-[160px] truncate">{room.address}</p>
                  </td>
                  <td className="px-4 py-3 text-right text-xs text-zinc-700 tabular-nums whitespace-nowrap">{formatPrice(room.price.pricePer4Hours)}</td>
                  <td className="px-4 py-3 text-right text-xs text-zinc-700 tabular-nums whitespace-nowrap">{formatPrice(room.price.pricePer9Hours)}</td>
                  <td className="px-4 py-3 text-right text-xs text-zinc-700 tabular-nums whitespace-nowrap">{formatPrice(room.price.priceOvernight13Hours)}</td>
                  <td className="px-4 py-3 text-right text-xs text-zinc-700 tabular-nums whitespace-nowrap">{formatPrice(room.price.dailyRate)}</td>
                  <td className="px-4 py-3 text-right text-xs text-zinc-700 tabular-nums whitespace-nowrap">{formatPrice(room.price.dailyRateHoliday)}</td>
                  <td className="px-4 py-3 text-center">
                    <button
                      onClick={() => handleToggleFeatured(room)}
                      title={room.featured ? "Bỏ nổi bật" : "Đánh dấu nổi bật"}
                      className={`text-lg transition-colors ${room.featured ? "text-amber-400 hover:text-amber-500" : "text-zinc-200 hover:text-amber-400"}`}
                    >
                      ★
                    </button>
                  </td>
                  {canEdit && (
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => handleToggleHidden(room)}
                          title={room.hidden ? "Hiện phòng" : "Ẩn phòng"}
                          className={`p-1.5 rounded-md transition-colors ${room.hidden ? "text-zinc-400 hover:bg-emerald-50 hover:text-emerald-600" : "text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700"}`}
                        >
                          {room.hidden ? (
                            <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9.88 9.88a3 3 0 1 0 4.24 4.24"/><path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68"/><path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61"/><line x1="2" x2="22" y1="2" y2="22"/></svg>
                          ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/></svg>
                          )}
                        </button>
                        <button
                          onClick={() => handleEdit(room)}
                          title="Chỉnh sửa"
                          className="p-1.5 rounded-md text-zinc-400 hover:bg-blue-50 hover:text-blue-600 transition-colors"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                        </button>
                        {isSuperAdmin && (
                          <button
                            onClick={() => handleDelete(room._id)}
                            title="Xóa"
                            className="p-1.5 rounded-md text-zinc-400 hover:bg-red-50 hover:text-red-600 transition-colors"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {rooms.length === 0 && (
          <div className="text-center py-16">
            <div className="w-12 h-12 rounded-xl bg-zinc-100 flex items-center justify-center mx-auto mb-3">
              <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#a1a1aa" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
            </div>
            <p className="text-sm font-medium text-zinc-500">Chưa có phòng nào</p>
            <p className="text-xs text-zinc-400 mt-1">Bấm "Thêm phòng mới" để bắt đầu</p>
          </div>
        )}
        <Pagination page={page} totalPages={totalPages} total={filteredRooms.length} pageSize={PAGE_SIZE} onChange={setPage} />
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg max-w-4xl w-full my-8">
            <div className="p-6 max-h-[85vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">
                  {editingRoom ? "Chỉnh sửa phòng" : "Thêm phòng mới"}
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ✕
                </button>
              </div>

              <form onSubmit={handleSave} className="space-y-6">
                {/* Basic Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tên phòng *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const name = e.target.value;
                        setFormData({ ...formData, name, slug: toSlug(name) });
                      }}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-coffee-500"
                      placeholder="Bloom"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Slug <span className="text-gray-400 font-normal">(tự động)</span>
                    </label>
                    <input
                      type="text"
                      readOnly
                      value={formData.slug}
                      className="w-full px-4 py-2 border rounded-lg bg-gray-50 text-gray-500 cursor-default"
                    />
                  </div>
                </div>

                {/* District */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Quận/Huyện *
                  </label>
                  <select
                    required
                    value={formData.district}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleDistrictChange(e.target.value)}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-coffee-500"
                  >
                    <option value="">Chọn quận/huyện</option>
                    {districts.map((d) => (
                      <option key={d} value={d}>
                        {d}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Address */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Địa chỉ đầy đủ *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.address}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setFormData({ ...formData, address: e.target.value })
                      }
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-coffee-500"
                      placeholder="Quận Tân Phú, TP. HCM"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Address (EN)
                    </label>
                    <input
                      type="text"
                      value={formData.addressEn}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setFormData({ ...formData, addressEn: e.target.value })
                      }
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-coffee-500"
                      placeholder="Tan Phu District, HCMC"
                    />
                  </div>
                </div>

                {/* Tọa độ bản đồ */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Vị trí trên bản đồ
                  </label>
                  <MapPicker
                    lat={formData.location?.lat as string}
                    lng={formData.location?.lng as string}
                    onChange={(loc: { lat: string; lng: string }) => setFormData(prev => ({ ...prev, location: loc }))}
                  />
                </div>

                {/* Images Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Hình ảnh phòng * (tự động nén, tối đa ~30 ảnh)
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleImagesUpload}
                      disabled={uploading}
                      className="hidden"
                      id="roomImages"
                    />
                    <label
                      htmlFor="roomImages"
                      className="cursor-pointer block text-center"
                    >
                      <div className="text-3xl mb-2">📸</div>
                      <p className="text-sm text-gray-600">
                        {uploading
                          ? "⏳ Đang xử lý..."
                          : "Click để chọn ảnh (kéo thả)"}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Ảnh sẽ tự động nén xuống 1200px, quality 70%
                      </p>
                    </label>

                    {formData.images.length > 0 && (
                      <div className="mt-3">
                        <p className="text-sm text-gray-600 mb-2">
                          Đã có {formData.images.length} ảnh
                        </p>
                        <div className="grid grid-cols-4 gap-2">
                          {formData.images.map((img, i) => (
                            <div key={i} className="relative">
                              <img
                                src={img}
                                alt={`Room ${i}`}
                                className="w-full h-20 object-cover rounded"
                              />
                              <button
                                type="button"
                                onClick={() => removeImage(i)}
                                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600 text-xs"
                              >
                                ✕
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Price Image */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ảnh bảng giá
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handlePriceImageUpload}
                      disabled={uploading}
                      className="hidden"
                      id="priceImage"
                    />
                    <label htmlFor="priceImage" className="cursor-pointer">
                      {formData.priceImage ? (
                        <img
                          src={formData.priceImage}
                          alt="Price"
                          className="mx-auto w-48 h-32 object-contain"
                        />
                      ) : (
                        <>
                          <div className="text-3xl mb-2">💰</div>
                          <p className="text-sm text-gray-600">
                            {uploading
                              ? "⏳ Đang xử lý..."
                              : "Click để chọn ảnh bảng giá"}
                          </p>
                        </>
                      )}
                    </label>
                  </div>
                </div>

                {/* Prices */}
                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Giá 4h *
                    </label>
                    <input
                      type="number"
                      required
                      value={formData.price.pricePer4Hours}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setFormData({
                          ...formData,
                          price: {
                            ...formData.price,
                            pricePer4Hours: e.target.value,
                          },
                        })
                      }
                      className="w-full px-4 py-2 border rounded-lg"
                      placeholder="320000"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Giá 9h *
                    </label>
                    <input
                      type="number"
                      required
                      value={formData.price.pricePer9Hours}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setFormData({
                          ...formData,
                          price: {
                            ...formData.price,
                            pricePer9Hours: e.target.value,
                          },
                        })
                      }
                      className="w-full px-4 py-2 border rounded-lg"
                      placeholder="515000"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Giá qua đêm (13h) *
                    </label>
                    <input
                      type="number"
                      required
                      value={formData.price.priceOvernight13Hours}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setFormData({
                          ...formData,
                          price: {
                            ...formData.price,
                            priceOvernight13Hours: e.target.value,
                          },
                        })
                      }
                      className="w-full px-4 py-2 border rounded-lg"
                      placeholder="420000"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Giá ngày thường *
                    </label>
                    <input
                      type="number"
                      required
                      value={formData.price.dailyRate}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setFormData({
                          ...formData,
                          price: {
                            ...formData.price,
                            dailyRate: e.target.value,
                          },
                        })
                      }
                      className="w-full px-4 py-2 border rounded-lg"
                      placeholder="900000"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Giá T7/CN (14h–12h) *
                    </label>
                    <input
                      type="number"
                      required
                      value={formData.price.dailyRateHoliday}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setFormData({
                          ...formData,
                          price: {
                            ...formData.price,
                            dailyRateHoliday: e.target.value,
                          },
                        })
                      }
                      className="w-full px-4 py-2 border rounded-lg"
                      placeholder="900000"
                    />
                  </div>
                </div>

                {/* Amenities */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tiện nghi
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 p-4 border rounded-lg">
                    {amenitiesList.map((amenity, index) => (
                      <label
                        key={index}
                        className="flex items-center space-x-2 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={formData.amenities.includes(amenity.vi)}
                          onChange={() => toggleAmenity(amenity)}
                          className="w-4 h-4 text-coffee-600 rounded focus:ring-coffee-500"
                        />
                        <span className="text-sm text-gray-700">
                          {amenity.vi}
                        </span>
                      </label>
                    ))}
                  </div>
                  {formData.amenities.length > 0 && (
                    <div className="mt-2 text-sm text-gray-600">
                      Đã chọn: {formData.amenities.join(", ")}
                    </div>
                  )}
                </div>

                {/* Descriptions */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mô tả (Tiếng Việt) *
                  </label>
                  <textarea
                    required
                    rows={4}
                    value={formData.description}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-coffee-500"
                    placeholder="Mô tả chi tiết về phòng..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description (English)
                  </label>
                  <textarea
                    rows={4}
                    value={formData.descriptionEn}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                      setFormData({
                        ...formData,
                        descriptionEn: e.target.value,
                      })
                    }
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-coffee-500"
                    placeholder="Detailed room description..."
                  />
                </div>

                {/* Check-in / Check-out Instructions */}
                <div className="border border-blue-100 rounded-xl p-4 bg-blue-50 space-y-4">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-lg">🔑</span>
                    <h3 className="font-semibold text-blue-900 text-sm">Hướng dẫn Check-in / Check-out</h3>
                    <span className="text-xs text-blue-500 ml-1">(gửi kèm email nhắc nhở trước 2 tiếng check-in)</span>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Hướng dẫn nhận phòng (Check-in) — Tiếng Việt
                    </label>
                    <textarea
                      rows={4}
                      value={formData.checkInInstructions}
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                        setFormData({ ...formData, checkInInstructions: e.target.value })
                      }
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-400 bg-white resize-y"
                      placeholder="Nhập hướng dẫn check-in tiếng Việt..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Check-in Instructions (English)
                    </label>
                    <textarea
                      rows={4}
                      value={formData.checkInInstructionsEn}
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                        setFormData({ ...formData, checkInInstructionsEn: e.target.value })
                      }
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-400 bg-white resize-y"
                      placeholder="Enter check-in instructions in English..."
                    />
                  </div>
                </div>

                {/* Video & Calendar */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Video URL
                    </label>
                    <input
                      type="url"
                      value={formData.video}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setFormData({ ...formData, video: e.target.value })
                      }
                      className="w-full px-4 py-2 border rounded-lg"
                      placeholder="https://youtube.com/..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Calendar Embed URL
                    </label>
                    <input
                      type="url"
                      value={formData.calendarEmbedUrl}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setFormData({
                          ...formData,
                          calendarEmbedUrl: e.target.value,
                        })
                      }
                      className="w-full px-4 py-2 border rounded-lg"
                      placeholder="https://calendar.google.com/..."
                    />
                  </div>
                </div>

                {/* Trang Hướng Dẫn Nhận Phòng */}
                <div className="border border-amber-100 rounded-xl p-4 bg-amber-50 space-y-4">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">🗝️</span>
                      <h3 className="font-semibold text-amber-900 text-sm">Trang Hướng Dẫn Nhận Phòng</h3>
                    </div>
                    {editingRoom && formData.checkInGuide?.roomNumber && (
                      <a
                        href={`${window.location.origin.replace("3001","3000").replace("3002","3000")}/guide/${editingRoom.slug}`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs text-amber-700 border border-amber-300 rounded px-2 py-1 hover:bg-amber-100 flex items-center gap-1"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                        Xem trang khách
                      </a>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Mã hộp khóa</label>
                      <input
                        type="text"
                        value={formData.checkInGuide?.lockBoxCode || ""}
                        onChange={(e) => setFormData({ ...formData, checkInGuide: { ...formData.checkInGuide, lockBoxCode: e.target.value } })}
                        className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-amber-400 bg-white"
                        placeholder="VD: 8602"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Vị trí hộp khóa</label>
                      <input
                        type="text"
                        value={formData.checkInGuide?.lockBoxLocation || ""}
                        onChange={(e) => setFormData({ ...formData, checkInGuide: { ...formData.checkInGuide, lockBoxLocation: e.target.value } })}
                        className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-amber-400 bg-white"
                        placeholder="VD: Hộp xám — bên phải cổng"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Số phòng</label>
                      <input
                        type="text"
                        value={formData.checkInGuide?.roomNumber || ""}
                        onChange={(e) => setFormData({ ...formData, checkInGuide: { ...formData.checkInGuide, roomNumber: e.target.value } })}
                        className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-amber-400 bg-white"
                        placeholder="VD: 203"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Thông tin đỗ xe</label>
                      <input
                        type="text"
                        value={formData.checkInGuide?.parkingInfo || ""}
                        onChange={(e) => setFormData({ ...formData, checkInGuide: { ...formData.checkInGuide, parkingInfo: e.target.value } })}
                        className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-amber-400 bg-white"
                        placeholder="VD: Tối đa 1 xe máy (miễn phí)"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Tên WiFi</label>
                      <input
                        type="text"
                        value={formData.checkInGuide?.wifiName || ""}
                        onChange={(e) => setFormData({ ...formData, checkInGuide: { ...formData.checkInGuide, wifiName: e.target.value } })}
                        className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-amber-400 bg-white"
                        placeholder="VD: HND house 5g"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Mật khẩu WiFi</label>
                      <input
                        type="text"
                        value={formData.checkInGuide?.wifiPassword || ""}
                        onChange={(e) => setFormData({ ...formData, checkInGuide: { ...formData.checkInGuide, wifiPassword: e.target.value } })}
                        className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-amber-400 bg-white"
                        placeholder="VD: 68686868"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Link Google Maps</label>
                    <input
                      type="url"
                      value={formData.checkInGuide?.googleMapsUrl || ""}
                      onChange={(e) => setFormData({ ...formData, checkInGuide: { ...formData.checkInGuide, googleMapsUrl: e.target.value } })}
                      className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-amber-400 bg-white"
                      placeholder="https://maps.google.com/..."
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Gợi ý tìm đường</label>
                    <input
                      type="text"
                      value={formData.checkInGuide?.addressHint || ""}
                      onChange={(e) => setFormData({ ...formData, checkInGuide: { ...formData.checkInGuide, addressHint: e.target.value } })}
                      className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-amber-400 bg-white"
                      placeholder='VD: Google Maps → Search "Lẩu cá Thanh Đa" → đi đến cuối hẻm'
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Video hướng dẫn mở cửa (Google Drive)</label>
                    <input
                      type="url"
                      value={formData.checkInGuide?.lockBoxVideoUrl || ""}
                      onChange={(e) => setFormData({ ...formData, checkInGuide: { ...formData.checkInGuide, lockBoxVideoUrl: e.target.value } })}
                      className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-amber-400 bg-white"
                      placeholder="https://drive.google.com/file/d/.../preview"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Hướng dẫn vào cổng</label>
                    <textarea
                      rows={2}
                      value={formData.checkInGuide?.gateInstructions || ""}
                      onChange={(e) => setFormData({ ...formData, checkInGuide: { ...formData.checkInGuide, gateInstructions: e.target.value } })}
                      className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-amber-400 bg-white resize-y"
                      placeholder="VD: Chạm chip vào máy quét ở cổng. Dắt xe vào, đi tới cuối hành lang đụng tường, quẹo trái."
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Hướng dẫn cầu thang / vào phòng</label>
                    <textarea
                      rows={2}
                      value={formData.checkInGuide?.buildingInstructions || ""}
                      onChange={(e) => setFormData({ ...formData, checkInGuide: { ...formData.checkInGuide, buildingInstructions: e.target.value } })}
                      className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-amber-400 bg-white resize-y"
                      placeholder="VD: Cầu thang sắt ngay sau khi quẹo trái. Lên lầu, ra hành lang ngoài trời, tìm biển số phòng."
                    />
                  </div>
                </div>

                {/* Buttons */}
                <div className="flex space-x-3 pt-4">
                  <button
                    type="submit"
                    disabled={uploading}
                    className="flex-1 bg-coffee-600 text-white py-3 px-4 rounded-lg hover:bg-coffee-700 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {uploading
                      ? "⏳ Đang xử lý..."
                      : editingRoom
                        ? "Cập nhật"
                        : "Thêm phòng"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="flex-1 bg-gray-200 text-gray-800 py-3 px-4 rounded-lg hover:bg-gray-300 font-semibold"
                  >
                    Hủy
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default RoomsPage;
